<script setup>
import {computed, ref, onMounted, watch} from 'vue'
import {useStore} from 'vuex'
import {useRouter} from 'vue-router'

import {ProjectListItem} from '@/components/project-list'
import {Spinner} from '@/components/ui/spinner'
import {Button} from '@/components/ui/button'
import {PlusCircle} from 'lucide-vue-next'

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

const store = useStore()
const router = useRouter()

const projects = computed(() => store.getters['project/getProjects'])
const selectedOrgId = computed(() => store.getters['org/getSelected'])
const isLoading = computed(() => store.getters['project/getIsLoading'])

const sortByChooses = [
  { value: 'az', label: 'A-Z' },
  { value: 'za', label: 'Z-A' },
  { value: 'created_at', label: 'Created At' },
  { value: 'last_activity', label: 'Last Activity' },
]
const sortBy = ref(sortByChooses[0].value)

const sortedProjects = computed(() => {
  const sortProjects = (projectsArray) => {
    return projectsArray.map(project => {
      if (project.children && Array.isArray(project.children)) {
        project.children = sortProjects(project.children);
      }
      return project;
    }).sort((a, b) => {
      if (sortBy.value === 'az') {
        return a.project_name.localeCompare(b.project_name)
      } else if (sortBy.value === 'za') {
        return b.project_name.localeCompare(a.project_name)
      } else if (sortBy.value === 'created_at') {
        return new Date(a.created_at) - new Date(b.created_at);
      } else if (sortBy.value === 'last_activity') {
        return new Date(b.last_activity) - new Date(a.last_activity);
      }
      return 0;
    });
  };

  // Filter projects by selected org only
  const orgProjects = projects.value
    .filter(i => i.organization_id === parseInt(selectedOrgId.value));

  return sortProjects(orgProjects);
});

const openedProjectIds = ref(localStorage.getItem('openedProjectIds') ? JSON.parse(localStorage.getItem('openedProjectIds')) : {})

const select = (project, level) => {
  if (!!openedProjectIds.value?.[project.project_id]) {
    delete openedProjectIds.value[project.project_id]
  } else {
    openedProjectIds.value[project.project_id] = {
      level: level,
      project: project
    }
  }

  localStorage.setItem('openedProjectIds', JSON.stringify(openedProjectIds.value))
}

const createProject = () => {
  router.push({ name: 'CreateProject', params: { org_id: selectedOrgId.value } })
}

// Fetch projects when component mounts
onMounted(async () => {
  if (selectedOrgId.value) {
    await store.dispatch('project/fetchProjects')
  }
})

// Watch for organization changes and fetch projects
watch(selectedOrgId, async (newOrgId) => {
  if (newOrgId) {
    await store.dispatch('project/fetchProjects')
  }
})
</script>

<template>
  <div class="px-2 flex items-center justify-between">
    <div class="flex items-center">
      <span class="mr-2 text-xs text-gray-500">Sort By:</span>
      <Select v-model="sortBy">
        <SelectTrigger class="flex-1 max-w-[150px] border-0 p-0 h-auto text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            <SelectItem v-for="item in sortByChooses" :value="item.value" :key="item.value">
              {{ item.label }}
            </SelectItem>
          </SelectGroup>
        </SelectContent>
      </Select>
    </div>
    <Button variant="ghost" size="sm" @click="createProject">
      <PlusCircle class="w-4 h-4 mr-1.5" aria-hidden="true" />
      <span>New Project</span>
    </Button>
  </div>

  <div class="border-b border-gray-200 py-1">
    <!-- Loading State -->
    <div v-if="isLoading" class="flex items-center justify-center py-8">
      <Spinner class="h-6 w-6" />
      <span class="ml-2 text-sm text-gray-500">Loading projects...</span>
    </div>

    <!-- Projects List -->
    <div v-else>
      <ProjectListItem
        v-for="project in sortedProjects"
        :key="project.project_id"
        :project="project"
        :opened-project-ids="openedProjectIds"
        @select="(project, level) => select(project, level + 1)"
      />
    </div>

    <div v-if="sortedProjects.length === 0" class="flex items-center justify-center py-8">
      No projects found
    </div>
  </div>
</template>
